import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { AdminNav } from "@/components/AdminNav";

const EMPTY_APPLICABILITY = {
  employment_status: "any",
  previous_work_experience: "any",
  career_stage: "any",
  education_status: "any",
  management_background: "any",
  career_direction: "any",
};

const FALLBACK_APPLICABILITY_FIELDS = {
  employment_status: {
    label: "Employment status",
    options: [
      { value: "any", label: "Any" },
      { value: "currently_working", label: "Currently working" },
      { value: "not_currently_working", label: "Not currently working" },
    ],
  },
  previous_work_experience: {
    label: "Previous work experience",
    options: [
      { value: "any", label: "Any" },
      { value: "has_previous_work_experience", label: "Has previous work experience" },
      { value: "no_previous_work_experience", label: "No previous work experience / first job" },
    ],
  },
  career_stage: {
    label: "Career stage",
    options: [
      { value: "any", label: "Any" },
      { value: "first_job", label: "First job / entering workforce" },
      { value: "early_career", label: "Early career" },
      { value: "experienced", label: "Experienced professional" },
    ],
  },
  education_status: {
    label: "Education status",
    options: [
      { value: "any", label: "Any" },
      { value: "student_or_recent_graduate", label: "Student or recent graduate" },
      { value: "not_student_or_recent_graduate", label: "Not a student / recent graduate" },
    ],
  },
  management_background: {
    label: "Management background",
    options: [
      { value: "any", label: "Any" },
      { value: "has_people_management", label: "Has people-management experience" },
      { value: "no_people_management", label: "No people-management experience" },
    ],
  },
  career_direction: {
    label: "Career direction",
    options: [
      { value: "any", label: "Any" },
      { value: "career_changer", label: "Changing career / field" },
      { value: "continuing_related_field", label: "Continuing in same / related field" },
    ],
  },
};

const EMPTY_FORM = {
  question_id: null,
  text: "",
  category: "",
  follow_up_eligible: true,
  asked_every_interview: false,
  applicability: { ...EMPTY_APPLICABILITY },
  active: true,
};

const TTS1_VOICES = ["alloy", "ash", "coral", "echo", "fable", "nova", "onyx", "sage", "shimmer"];
const PREVIEW_TEXT =
  "Thanks for joining me today. Tell me a little about yourself and the experience you would bring to this role.";
const ADD_CATEGORY_VALUE = "__add_category__";

function audioLabel(meta) {
  if (!meta) return "Not generated";
  return `${meta.voice || "voice"} · ${meta.model || "model"}`;
}

function findLabel(fields, fieldName, value) {
  const field = fields?.[fieldName];
  return field?.options?.find((option) => option.value === value)?.label || value;
}

export default function AdminStandardQuestions() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [audioBusyId, setAudioBusyId] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [categories, setCategories] = useState([]);
  const [addingCategory, setAddingCategory] = useState(false);
  const [voiceInfo, setVoiceInfo] = useState({ voices: [], defaults: {}, model: "" });
  const [applicabilityInfo, setApplicabilityInfo] = useState({
    fields: FALLBACK_APPLICABILITY_FIELDS,
    interview_structure: { mandatory_standard: 3, advanced_flexible_primary: 7, quick_flexible_primary: 3 },
  });
  const [femaleVoice, setFemaleVoice] = useState("nova");
  const [maleVoice, setMaleVoice] = useState("echo");
  const [showArchived, setShowArchived] = useState(false);
  const [mandatoryCount, setMandatoryCount] = useState(0);
  const [mandatoryLimit, setMandatoryLimit] = useState(3);

  const supportedVoices = useMemo(() => {
    const backend = voiceInfo.voices || [];
    if (String(voiceInfo.model || "").startsWith("tts-1")) {
      return backend.filter((v) => TTS1_VOICES.includes(v));
    }
    return backend;
  }, [voiceInfo]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [q, v, a, c] = await Promise.all([
        api.get("/admin/standard-questions"),
        api.get("/admin/standard-questions/voices"),
        api.get("/admin/standard-questions/applicability-options"),
        api.get("/admin/standard-questions/categories"),
      ]);
      setQuestions(q.data?.questions || []);
      setMandatoryCount(q.data?.mandatory_count || 0);
      setMandatoryLimit(q.data?.mandatory_limit || 3);
      setVoiceInfo(v.data || { voices: [], defaults: {}, model: "" });
      setApplicabilityInfo(a.data || { fields: FALLBACK_APPLICABILITY_FIELDS });
      setCategories(c.data?.categories || []);
      if (v.data?.defaults?.female) setFemaleVoice(v.data.defaults.female);
      if (v.data?.defaults?.male) setMaleVoice(v.data.defaults.male);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const visibleQuestions = useMemo(
    () => questions.filter((q) => showArchived || q.active),
    [questions, showArchived],
  );

  const resetForm = () => {
    setAddingCategory(false);
    setForm({ ...EMPTY_FORM, applicability: { ...EMPTY_APPLICABILITY } });
  };

  const editQuestion = (q) => {
    setAddingCategory(false);
    setForm({
      question_id: q.question_id,
      text: q.text || "",
      category: q.category || "",
      follow_up_eligible: Boolean(q.follow_up_eligible),
      asked_every_interview: Boolean(q.asked_every_interview),
      applicability: { ...EMPTY_APPLICABILITY, ...(q.applicability || {}) },
      active: q.active !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveQuestion = async (event) => {
    event.preventDefault();
    const text = form.text.trim();
    if (text.length < 3) {
      toast.error("Enter the interview question before saving.");
      return;
    }

    setSaving(true);
    const body = {
      text,
      category: form.category.trim() || null,
      follow_up_eligible: Boolean(form.follow_up_eligible),
      asked_every_interview: Boolean(form.asked_every_interview),
      applicability: { ...EMPTY_APPLICABILITY, ...form.applicability },
      active: Boolean(form.active),
    };

    try {
      if (form.question_id) {
        await api.patch(`/admin/standard-questions/${encodeURIComponent(form.question_id)}`, body);
        toast.success("Standard question updated.");
      } else {
        await api.post("/admin/standard-questions", body);
        toast.success("Standard question added.");
      }
      resetForm();
      await refresh();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    } finally {
      setSaving(false);
    }
  };

  const archiveQuestion = async (q) => {
    if (!window.confirm(`Archive this standard question?\n\n${q.text}`)) return;
    try {
      await api.delete(`/admin/standard-questions/${encodeURIComponent(q.question_id)}`);
      toast.success("Question archived.");
      if (form.question_id === q.question_id) resetForm();
      await refresh();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    }
  };

  const playBlob = (blob) => {
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    const cleanup = () => URL.revokeObjectURL(url);
    audio.addEventListener("ended", cleanup, { once: true });
    audio.addEventListener("error", cleanup, { once: true });
    audio.play().catch(() => {
      cleanup();
      toast.error("Audio could not be played in this browser.");
    });
  };

  const previewVoice = async (voice, slot) => {
    setPreviewBusy(slot);
    try {
      const response = await api.post(
        "/admin/standard-questions/voice-preview",
        { voice, text: PREVIEW_TEXT },
        { responseType: "blob" },
      );
      playBlob(response.data);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    } finally {
      setPreviewBusy(null);
    }
  };

  // Active questions whose saved audio is missing, or wasn't made in the saved
  // interview voice (Admin → Voice Check: voice and tone, compared by fingerprint).
  // Interviews never play those clips; they generate the question live instead,
  // until it is regenerated here.
  const staleAudio = useMemo(() => questions.filter((q) => {
    if (!q.active || !voiceInfo.fingerprints) return false;
    return ["female", "male"].some((g) => q.audio?.[g]?.profile !== voiceInfo.fingerprints[g]);
  }), [questions, voiceInfo]);
  const [bulkAudio, setBulkAudio] = useState(null); // { done, total } while regenerating

  const regenerateStaleAudio = async () => {
    const todo = staleAudio;
    if (!todo.length) return;
    if (!window.confirm(`Regenerate the female and male audio for ${todo.length} question(s) in the interview voice?`)) return;
    let failed = 0;
    setBulkAudio({ done: 0, total: todo.length });
    for (let i = 0; i < todo.length; i++) {
      try {
        await api.post(`/admin/standard-questions/${encodeURIComponent(todo[i].question_id)}/audio/generate`, {}, { timeout: 90000 });
      } catch {
        failed += 1;
      }
      setBulkAudio({ done: i + 1, total: todo.length });
    }
    setBulkAudio(null);
    if (failed) toast.error(`${failed} of ${todo.length} could not be regenerated. Try again.`);
    else toast.success(`Audio regenerated for ${todo.length} question(s).`);
    await refresh();
  };

  const generateAudio = async (q) => {
    setAudioBusyId(q.question_id);
    try {
      await api.post(`/admin/standard-questions/${encodeURIComponent(q.question_id)}/audio/generate`, {});
      toast.success("Female and male audio generated and saved.");
      await refresh();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    } finally {
      setAudioBusyId(null);
    }
  };

  const playSavedAudio = async (q, gender) => {
    const key = `${q.question_id}:${gender}`;
    setAudioBusyId(key);
    try {
      const response = await api.get(
        `/admin/standard-questions/${encodeURIComponent(q.question_id)}/audio/${gender}`,
        { responseType: "blob" },
      );
      playBlob(response.data);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    } finally {
      setAudioBusyId(null);
    }
  };

  const fields = applicabilityInfo.fields || FALLBACK_APPLICABILITY_FIELDS;
  const structure = applicabilityInfo.interview_structure || {};

  return (
    <div className="min-h-screen w-full bg-[color:var(--bg-base)]" data-testid="admin-standard-questions">
      <AdminNav title="Standard Interview Questions" />

      <main className="max-w-7xl mx-auto p-6 space-y-6">
        <section className="rounded-2xl border border-sky-200 bg-sky-50 px-5 py-4">
          <div className="text-xs font-semibold text-sky-900">Primary interview planning structure</div>
          <div className="mt-1 text-sm text-sky-950">
            <strong>Advanced:</strong> {structure.mandatory_standard ?? 3} benchmark Standard + {structure.advanced_flexible_primary ?? 7} flexible slots. <strong>Quick:</strong> {structure.mandatory_standard ?? 3} benchmark Standard + {structure.quick_flexible_primary ?? 3} flexible slots.
          </div>
          <div className="mt-1 text-xs text-sky-800">
            Flexible slots can be Optional Standard or Dynamic questions. There is no fixed quota; selection is evidence- and role-driven.
          </div>
          <div className="mt-1 text-xs text-sky-800">
            Mandatory standard slots used: <strong>{mandatoryCount} of {mandatoryLimit}</strong>.
          </div>
        </section>

        <section className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
          <form onSubmit={saveQuestion} className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[color:var(--brand)]">Question Library</div>
                <h2 className="mt-1 text-lg font-semibold text-[color:var(--text-primary)]">
                  {form.question_id ? "Edit standard question" : "Add standard question"}
                </h2>
                <p className="mt-1 text-xs text-[color:var(--text-secondary)]">
                  Structured conditions tell the live selection engine when a question is appropriate.
                </p>
              </div>
              {form.question_id && (
                <button type="button" onClick={resetForm} className="text-xs text-[color:var(--brand)] hover:underline">Add new instead</button>
              )}
            </div>

            <label className="block">
              <span className="text-xs font-medium text-[color:var(--text-primary)]">Question transcript</span>
              <textarea
                value={form.text}
                onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
                rows={4}
                maxLength={1000}
                className="mt-1 w-full rounded-xl border border-[color:var(--border-stone)] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--brand)]/20"
                placeholder="e.g. What do you enjoy most about your current position?"
                data-testid="standard-question-text"
              />
            </label>

            <div className="block">
              <span className="text-xs font-medium text-[color:var(--text-primary)]">Category / competency</span>
              <select
                value={addingCategory ? ADD_CATEGORY_VALUE : form.category}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === ADD_CATEGORY_VALUE) {
                    setAddingCategory(true);
                    setForm((f) => ({ ...f, category: "" }));
                  } else {
                    setAddingCategory(false);
                    setForm((f) => ({ ...f, category: value }));
                  }
                }}
                className="mt-1 w-full rounded-xl border border-[color:var(--border-stone)] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--brand)]/20"
                data-testid="standard-question-category-select"
              >
                <option value="">Select a category…</option>
                {categories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
                <option value={ADD_CATEGORY_VALUE}>Add Category…</option>
              </select>
              {addingCategory && (
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  maxLength={120}
                  autoFocus
                  className="mt-2 w-full rounded-xl border border-[color:var(--border-stone)] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--brand)]/20"
                  placeholder="Enter new category name"
                  data-testid="standard-question-category-new"
                />
              )}
              <div className="mt-1 text-[11px] text-[color:var(--text-secondary)]">
                Categories are shared across the Standard Question library so planner labels remain consistent.
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <Toggle
                label="Follow-up eligible"
                checked={form.follow_up_eligible}
                onChange={(checked) => setForm((f) => ({ ...f, follow_up_eligible: checked }))}
                hint="AI may ask a response-based follow-up."
                testId="standard-question-follow-up"
              />
              <Toggle
                label="Asked every interview"
                checked={form.asked_every_interview}
                onChange={(checked) => setForm((f) => ({ ...f, asked_every_interview: checked }))}
                hint={`Uses one of ${mandatoryLimit} mandatory slots and overrides applicability conditions.`}
                testId="standard-question-required"
              />
              <Toggle
                label="Active"
                checked={form.active}
                onChange={(checked) => setForm((f) => ({ ...f, active: checked }))}
                hint="Available to the live selection engine."
                testId="standard-question-active"
              />
            </div>

            <section className={`rounded-xl border p-4 ${form.asked_every_interview ? "border-sky-200 bg-sky-50/60" : "border-[color:var(--border-stone)]"}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="text-xs font-semibold text-[color:var(--text-primary)]">AI applicability</div>
                  <div className="mt-0.5 text-[11px] text-[color:var(--text-secondary)]">
                    All non-Any conditions must match the candidate résumé profile.
                  </div>
                </div>
                {form.asked_every_interview && (
                  <Pill tone="required">Overridden by Asked every interview</Pill>
                )}
              </div>

              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {Object.entries(fields).map(([fieldName, field]) => (
                  <label key={fieldName} className="block">
                    <span className="text-[11px] font-medium text-[color:var(--text-primary)]">{field.label}</span>
                    <select
                      value={form.applicability?.[fieldName] || "any"}
                      disabled={form.asked_every_interview}
                      onChange={(e) => setForm((f) => ({
                        ...f,
                        applicability: { ...f.applicability, [fieldName]: e.target.value },
                      }))}
                      className="mt-1 w-full rounded-xl border border-[color:var(--border-stone)] bg-white px-3 py-2 text-xs disabled:bg-slate-100 disabled:text-slate-500"
                      data-testid={`standard-question-applicability-${fieldName}`}
                    >
                      {(field.options || []).map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
              {form.asked_every_interview && (
                <div className="mt-3 text-[11px] text-sky-800">
                  These conditions are retained but ignored while this question is mandatory.
                </div>
              )}
            </section>

            <div className="flex justify-end gap-2">
              {form.question_id && (
                <button type="button" onClick={resetForm} className="rounded-full border border-[color:var(--border-stone)] px-4 py-2 text-xs font-medium">Cancel</button>
              )}
              <button
                type="submit"
                disabled={saving}
                className="rounded-full bg-[color:var(--brand)] px-5 py-2 text-xs font-semibold text-white hover:bg-[color:var(--brand-hover)] disabled:opacity-50"
                data-testid="standard-question-save"
              >
                {saving ? "Saving…" : form.question_id ? "Save changes" : "Add question"}
              </button>
            </div>
          </form>

          <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 space-y-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[color:var(--brand)]">Voice Lab</div>
              <h2 className="mt-1 text-lg font-semibold text-[color:var(--text-primary)]">Interviewer voices</h2>
              <p className="mt-1 text-xs text-[color:var(--text-secondary)]">
                {voiceInfo.provider === "elevenlabs"
                  ? <>The interview voice is ElevenLabs, model <strong>{voiceInfo.model || "—"}</strong>.</>
                  : <>Preview alternatives without changing the approved interview voices. Active model: <strong>{voiceInfo.model || "—"}</strong>.</>}
              </p>
            </div>

            {voiceInfo.provider === "elevenlabs" ? (
              <div className="space-y-2 text-sm" data-testid="voice-lab-elevenlabs">
                <div>Female: <strong>{voiceInfo.names?.female || "—"}</strong></div>
                <div>Male: <strong>{voiceInfo.names?.male || "—"}</strong></div>
                <p className="text-xs text-[color:var(--text-secondary)]">
                  Preview or change these voices in <Link to="/admin/voice-check" className="underline">Voice Check</Link> (step 1b).
                </p>
              </div>
            ) : (<>
            <VoicePicker
              label="Female voice preview"
              value={femaleVoice}
              options={supportedVoices}
              onChange={setFemaleVoice}
              onPreview={() => previewVoice(femaleVoice, "female")}
              busy={previewBusy === "female"}
              testId="female-voice-select"
            />
            <VoicePicker
              label="Male voice preview"
              value={maleVoice}
              options={supportedVoices}
              onChange={setMaleVoice}
              onPreview={() => previewVoice(maleVoice, "male")}
              busy={previewBusy === "male"}
              testId="male-voice-select"
            />
            </>)}

            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] leading-relaxed text-emerald-900">
              Saved standard-question audio uses the interview voice: <strong>{voiceInfo.names?.female || "—"}</strong> for Female and <strong>{voiceInfo.names?.male || "—"}</strong> for Male. Generate once, save it, and reuse it during interviews.
            </div>
          </section>
        </section>

        <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[color:var(--border-stone)] px-5 py-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[color:var(--text-primary)]">Stored questions</div>
              <div className="text-xs text-[color:var(--text-secondary)] mt-0.5">
                {loading ? "Loading…" : `${visibleQuestions.length} shown · ${questions.length} total`}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-[color:var(--text-secondary)]">
                <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
                Show archived
              </label>
              {(staleAudio.length > 0 || bulkAudio) && (
                <button
                  type="button"
                  onClick={regenerateStaleAudio}
                  disabled={Boolean(bulkAudio)}
                  title={`Saved audio that is missing, or not in the interview voice (${voiceInfo.model || "current model"}), is played live until regenerated.`}
                  className="rounded-full border border-[color:var(--brand)] text-[color:var(--brand)] px-3 py-1.5 text-xs font-medium disabled:opacity-60"
                  data-testid="sq-regenerate-stale-audio"
                >
                  {bulkAudio ? `Regenerating ${bulkAudio.done} of ${bulkAudio.total}…` : `Regenerate audio (${staleAudio.length})`}
                </button>
              )}
              <button type="button" onClick={refresh} className="rounded-full border border-[color:var(--border-stone)] px-3 py-1.5 text-xs font-medium">Refresh</button>
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-sm text-[color:var(--text-secondary)]">Loading standard questions…</div>
          ) : visibleQuestions.length === 0 ? (
            <div className="p-8 text-sm text-[color:var(--text-secondary)]" data-testid="standard-questions-empty">No standard questions yet. Add the first one above.</div>
          ) : (
            <div className="divide-y divide-[color:var(--border-stone)]">
              {visibleQuestions.map((q) => (
                <QuestionRow
                  key={q.question_id}
                  question={q}
                  fields={fields}
                  onEdit={() => editQuestion(q)}
                  onArchive={() => archiveQuestion(q)}
                  onGenerate={() => generateAudio(q)}
                  onPlayFemale={() => playSavedAudio(q, "female")}
                  onPlayMale={() => playSavedAudio(q, "male")}
                  busyId={audioBusyId}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function Toggle({ label, checked, onChange, hint, testId }) {
  return (
    <label className="rounded-xl border border-[color:var(--border-stone)] p-3 cursor-pointer">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold text-[color:var(--text-primary)]">{label}</span>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
      </div>
      <div className="mt-1 text-[11px] leading-relaxed text-[color:var(--text-secondary)]">{hint}</div>
    </label>
  );
}

function VoicePicker({ label, value, options, onChange, onPreview, busy, testId }) {
  return (
    <div>
      <div className="text-xs font-medium text-[color:var(--text-primary)]">{label}</div>
      <div className="mt-1 flex gap-2">
        <select value={value} onChange={(e) => onChange(e.target.value)} className="min-w-0 flex-1 rounded-xl border border-[color:var(--border-stone)] bg-white px-3 py-2 text-sm" data-testid={testId}>
          {options.map((voice) => <option key={voice} value={voice}>{voice}</option>)}
        </select>
        <button type="button" onClick={onPreview} disabled={!value || busy} className="rounded-full border border-[color:var(--brand)] px-4 py-2 text-xs font-medium text-[color:var(--brand)] disabled:opacity-50">
          {busy ? "Generating…" : "Preview"}
        </button>
      </div>
    </div>
  );
}

function Pill({ children, tone = "neutral" }) {
  const styles = {
    yes: "border-emerald-200 bg-emerald-50 text-emerald-800",
    no: "border-slate-200 bg-slate-50 text-slate-600",
    required: "border-sky-200 bg-sky-50 text-sky-800",
    inactive: "border-amber-200 bg-amber-50 text-amber-800",
    neutral: "border-[color:var(--border-stone)] bg-white text-[color:var(--text-secondary)]",
  };
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${styles[tone]}`}>{children}</span>;
}

function QuestionRow({ question: q, fields, onEdit, onArchive, onGenerate, onPlayFemale, onPlayMale, busyId }) {
  const generating = busyId === q.question_id;
  const femaleBusy = busyId === `${q.question_id}:female`;
  const maleBusy = busyId === `${q.question_id}:male`;
  const conditions = Object.entries(q.applicability || {})
    .filter(([, value]) => value && value !== "any")
    .map(([fieldName, value]) => findLabel(fields, fieldName, value));

  return (
    <div className={`p-5 ${q.active ? "" : "bg-slate-50/60"}`} data-testid={`standard-question-${q.question_id}`}>
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Pill tone={q.follow_up_eligible ? "yes" : "no"}>Follow-up {q.follow_up_eligible ? "YES" : "NO"}</Pill>
            <Pill tone={q.asked_every_interview ? "required" : "neutral"}>Every interview {q.asked_every_interview ? "YES" : "NO"}</Pill>
            {!q.active && <Pill tone="inactive">Archived</Pill>}
            {q.category && <Pill>{q.category}</Pill>}
          </div>
          <div className="text-sm font-medium leading-relaxed text-[color:var(--text-primary)]">{q.text}</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {q.asked_every_interview ? (
              <Pill tone="required">Applicability overridden</Pill>
            ) : conditions.length ? (
              conditions.map((label) => <Pill key={label}>{label}</Pill>)
            ) : (
              <Pill>Any candidate</Pill>
            )}
          </div>
          <div className="mt-2 font-mono text-[10px] text-[color:var(--text-secondary)]">{q.question_id}</div>
        </div>

        <div className="flex flex-wrap gap-2 xl:justify-end">
          <button type="button" onClick={onEdit} className="rounded-full border border-[color:var(--border-stone)] px-3 py-1.5 text-xs font-medium">Edit</button>
          {q.active && <button type="button" onClick={onArchive} className="rounded-full border border-amber-300 px-3 py-1.5 text-xs font-medium text-amber-800">Archive</button>}
          <button type="button" onClick={onGenerate} disabled={generating} className="rounded-full bg-[color:var(--brand)] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">
            {generating ? "Generating…" : q.audio?.female || q.audio?.male ? "Regenerate audio" : "Generate audio"}
          </button>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <AudioCard label="Female audio" meta={q.audio?.female} onPlay={onPlayFemale} busy={femaleBusy} />
        <AudioCard label="Male audio" meta={q.audio?.male} onPlay={onPlayMale} busy={maleBusy} />
      </div>
    </div>
  );
}

function AudioCard({ label, meta, onPlay, busy }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-[color:var(--border-stone)] px-3 py-2">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">{label}</div>
        <div className="mt-0.5 truncate text-xs font-medium text-[color:var(--text-primary)]">{audioLabel(meta)}</div>
        {meta?.generated_at && <div className="mt-0.5 text-[10px] text-[color:var(--text-secondary)]">{new Date(meta.generated_at).toLocaleString()}</div>}
      </div>
      <button type="button" onClick={onPlay} disabled={!meta || busy} className="shrink-0 rounded-full border border-[color:var(--border-stone)] px-3 py-1.5 text-xs font-medium disabled:opacity-40">
        {busy ? "Loading…" : "Play"}
      </button>
    </div>
  );
}
